/**
 * Launch a Trigger.dev task through the `trigger-task` Edge Function.
 *
 * The browser never holds the Trigger.dev secret: the Edge Function checks
 * the caller's staff role, enforces its task allowlist and forwards the
 * payload. On success Trigger.dev answers `{ id: "run_…" }`, which the
 * Edge Function relays with HTTP 202. Every failure mode is translated into
 * a sentence the user can act on — nothing is swallowed.
 */
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import { supabase } from './supabase';

export type TriggerTaskId =
  | 'proposal-pdf-generate'
  | 'm365-send-proposal'
  | 'sla-report-generate';

export interface TriggerTaskResult {
  /** Trigger.dev run id when the API returned one. */
  runId: string | null;
  /** Raw response body from the Edge Function (Trigger.dev API passthrough). */
  raw: unknown;
}

export class TriggerTaskError extends Error {
  readonly status: number | null;
  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'TriggerTaskError';
    this.status = status;
  }
}

const MAX_DETAIL = 300;

function trimDetail(text: string): string {
  const t = text.trim();
  return t.length > MAX_DETAIL ? `${t.slice(0, MAX_DETAIL - 1)}…` : t;
}

function extractDetail(text: string): string {
  if (!text) return '';
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      for (const key of ['error', 'message', 'detail']) {
        const v = o[key];
        if (typeof v === 'string' && v.trim()) return trimDetail(v);
        if (v && typeof v === 'object' && typeof (v as { message?: unknown }).message === 'string') {
          return trimDetail((v as { message: string }).message);
        }
      }
      return trimDetail(JSON.stringify(parsed));
    }
  } catch {
    // not JSON — fall through to the raw text
  }
  return trimDetail(text);
}

async function describeFunctionError(error: unknown): Promise<TriggerTaskError> {
  if (error instanceof FunctionsHttpError) {
    const res = error.context as Response | undefined;
    const status = typeof res?.status === 'number' ? res.status : null;
    let detail = '';
    try {
      detail = res ? extractDetail(await res.text()) : '';
    } catch {
      detail = '';
    }
    if (status === 401) {
      return new TriggerTaskError('Your session was rejected by trigger-task. Sign out and sign in again.', status);
    }
    if (status === 403) {
      return new TriggerTaskError('Your account is not permitted to launch background tasks (staff role required).', status);
    }
    if (status === 404) {
      return new TriggerTaskError('The trigger-task Edge Function is not deployed on this Supabase project.', status);
    }
    if (status === 500 && /missing function configuration/i.test(detail)) {
      return new TriggerTaskError(
        'trigger-task is deployed but its secrets (TRIGGER_SECRET_KEY / Supabase keys) are not set.',
        status,
      );
    }
    const suffix = detail ? `: ${detail}` : '';
    return new TriggerTaskError(
      `Task launch failed${status !== null ? ` (HTTP ${status})` : ''}${suffix}`,
      status,
    );
  }
  if (error instanceof FunctionsFetchError) {
    return new TriggerTaskError(
      'Could not reach the trigger-task Edge Function. It may not be deployed on this Supabase project, or the request was blocked.',
    );
  }
  if (error instanceof FunctionsRelayError) {
    return new TriggerTaskError('Supabase could not relay the request to the trigger-task Edge Function.');
  }
  if (error instanceof Error) return new TriggerTaskError(error.message);
  return new TriggerTaskError('Task launch failed for an unknown reason.');
}

export async function invokeTriggerTask(
  task: TriggerTaskId,
  payload: Record<string, unknown>,
  idempotencyKey?: string,
): Promise<TriggerTaskResult> {
  const { data, error } = await supabase.functions.invoke('trigger-task', {
    body: { task, payload, ...(idempotencyKey ? { idempotencyKey } : {}) },
  });
  if (error) throw await describeFunctionError(error);

  let body: unknown = data;
  if (typeof data === 'string') {
    try {
      body = JSON.parse(data);
    } catch {
      body = data;
    }
  }
  const runId =
    body && typeof body === 'object' && typeof (body as { id?: unknown }).id === 'string'
      ? (body as { id: string }).id
      : null;
  return { runId, raw: body };
}

/** Message for any error thrown while launching or following a task. */
export function describeTaskError(err: unknown, fallback: string): string {
  if (err instanceof TriggerTaskError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
